/**
 * DrawerBottom Custom Element
 * 
 * A bottom drawer component that can be opened and closed with touch gestures.
 * The drawer slides up from the bottom of the screen and can be dismissed by
 * swiping down or by clicking the overlay.
 * 
 * Usage:
 *   <drawer-bottom>Content goes here</drawer-bottom>
 * 
 * API:
 *   - open(): Opens the drawer
 *   - close(): Closes the drawer
 *   - Attributes: 
 *     - open: When present, drawer is open
 * 
 */

class DrawerBottom extends HTMLElement {
  constructor() {
    super();
    this.isDragging = false;
    this.touchStartY = 0;
  }

  connectedCallback() {
    this.overlay = document.getElementById('drawer-overlay');
    this.overlaySearchModal = document.querySelector('.drawer-overlay-search');
    this.overlay?.addEventListener('click', () => this.close());
    this.overlaySearchModal?.addEventListener('click', () => this.close());

    // Find the header element for drag-to-close functionality
    this.headerElement = this.querySelector('.quick-add-modal__header');
    
    if (this.headerElement) {
      // Add a visual indicator class to show it's grabbable
      this.headerElement.classList.add('grabable');
      
      // Add drag events only to the header element for closing
      this.headerElement.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: true });
      this.headerElement.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false });
      this.headerElement.addEventListener('touchend', this.handleTouchEnd.bind(this));
      
      this.headerElement.addEventListener('mousedown', this.handleMouseDown.bind(this));
      document.addEventListener('mousemove', this.handleMouseMove.bind(this));
      document.addEventListener('mouseup', this.handleMouseUp.bind(this));
    }

    // Check for open attribute on initialization
    if (this.hasAttribute('open')) {
      this.open();
    }
  }

  disconnectedCallback() {
    // Clean up overlay event listener
    if (this.overlay) {
      this.overlay.removeEventListener('click', this.close);
    }

    // Clean up search overlay event listener
    if (this.overlaySearchModal) {
      this.overlaySearchModal.removeEventListener('click', this.close);
    }
    
    // Clean up header element event listeners
    if (this.headerElement) {
      this.headerElement.removeEventListener('touchstart', this.handleTouchStart);
      this.headerElement.removeEventListener('touchmove', this.handleTouchMove);
      this.headerElement.removeEventListener('touchend', this.handleTouchEnd);
      this.headerElement.removeEventListener('mousedown', this.handleMouseDown);
    }
    
    // Clean up document event listeners
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
  }

  static get observedAttributes() {
    return ['open'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'open') {
      if (newValue !== null) {
        // open attribute was added
        this.openWithoutSettingAttribute();
      } else {
        // open attribute was removed
        this.closeWithoutRemovingAttribute();
      }
    }
  }

  open() {
    if (!this.hasAttribute('open')) {
      this.setAttribute('open', '');
      this.overlay.style.display = "flex";
    } else {
      this.openWithoutSettingAttribute();
    }
  }
  openWithoutSettingAttribute() {
    this.classList.add('open');
    this.overlay.classList.add('open');
    document.body.style.overflow = 'hidden';
    // Dispatch event
    this.dispatchEvent(
      new CustomEvent('drawer-opened', {
        bubbles: true,
      })
    );
  }

  close() {
    if (this.hasAttribute('open')) {
      this.removeAttribute('open');
      // Dispatch a custom event for custom functionality at search modal
      const modalCloseEvent = new CustomEvent('modal:closed');
      document.dispatchEvent(modalCloseEvent);
      this.overlay.style.display = "flex";
    } else {
      this.closeWithoutRemovingAttribute();
    }
  }

  closeWithoutRemovingAttribute() {
    this.style.transform = 'translateY(100%)';

    setTimeout(() => {
      this.classList.remove('open');
      this.overlay.classList.remove('open');
      if (this.overlaySearchModal) {
        this.overlaySearchModal.classList.remove('open');
      }
      document.body.style.overflow = '';
      this.style.transform = '';

      // Dispatch event
      this.dispatchEvent(
        new CustomEvent('drawer-closed', {
          bubbles: true,
        })
      );
    }, 300);
  }

  handleScroll() {
    // Check if we're at the top or bottom of the scroll
    this.isAtTop = this.scrollTop <= 10; // A small threshold to consider as "at top"
    this.isAtBottom = this.scrollHeight - this.scrollTop - this.clientHeight <= 10; // Small threshold for bottom
    this.isScrollable = this.scrollHeight > this.clientHeight;
  }

  handleTouchStart(e) {
    if (!this.classList.contains('open')) return;
    
    this.touchStartY = e.touches[0].clientY;
    this.isDragging = true;
    this.style.transition = 'none';
  }

  handleTouchMove(e) {
    if (!this.isDragging) return;

    const touchY = e.touches[0].clientY;
    const deltaY = touchY - this.touchStartY;

    // Only allow dragging down (positive deltaY)
    if (deltaY > 0) {
      this.style.transform = `translateY(${deltaY}px)`;
      e.preventDefault();
    }
  }

  handleTouchEnd() {
    if (!this.isDragging) return;

    this.isDragging = false;
    this.style.transition = 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)';

    const threshold = this.offsetHeight * 0.2;
    const currentTranslate = this.getTranslateY();

    if (currentTranslate > threshold) {
      this.close();
    } else {
      this.style.transform = '';
    }
  }

  getTranslateY() {
    const style = window.getComputedStyle(this);
    const transform = style.transform;
    let matrix = new DOMMatrix(transform);
    return matrix.m42;
  }

  // Mouse event handlers for desktop users
  handleMouseDown(e) {
    if (!this.classList.contains('open')) return;
    
    this.touchStartY = e.clientY;
    this.isDragging = true;
    this.style.transition = 'none';
  }

  handleMouseMove(e) {
    if (!this.isDragging) return;

    const deltaY = e.clientY - this.touchStartY;

    // Only allow dragging down (positive deltaY)
    if (deltaY > 0) {
      this.style.transform = `translateY(${deltaY}px)`;
      e.preventDefault();
    }
  }

  handleMouseUp(e) {
    if (!this.isDragging) return;

    this.isDragging = false;
    this.style.transition = 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)';

    const threshold = this.offsetHeight * 0.2;
    const currentTranslate = this.getTranslateY();

    if (currentTranslate > threshold) {
      this.close();
    } else {
      this.style.transform = '';
    }
  }
}

customElements.define('drawer-bottom', DrawerBottom);
